import styles from './template.css';
import template from './template';
import {AoflElement, customElement, property} from '@aofl/element';
import {countries} from '@modules/phone-input/countries';
import {mapStatePropertiesMixin} from '@aofl/map-state-properties-mixin';
import {storeInstance} from '@aofl/store/dist/legacy';
import {sdoNs} from '@modules/constants-enumerate';
import '@modules/phone-input';

/**
 * @summary Phone Number Input
 * @extends {AoflElement}
 */
@customElement('phone-input')
class PhoneInput extends mapStatePropertiesMixin(AoflElement) {
  /**
   * Creates a phone input field.
   */
  constructor() {
    super();
    this.storeInstance = storeInstance;
    const state = this.storeInstance.getState();
    this.countries = countries;
    // rename Taiwan to Republic of China in China or if the current language is traditional Chinese
    if (state[sdoNs.SESSION].locale === 'zh-CN' || this.groupCountry?.toLowerCase() === 'cn') {
      for (let i in this.countries) {
        if (this.countries[i].iso === 'tw') {
          this.countries[i].name = 'Republic of China';
        }
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    const state = this.storeInstance.getState();
    // find geoip iso
    const locale = state[sdoNs.LOCALE_DD];
    const localeIso = locale.geoipCountry.toLowerCase();
    let foundIso = null;
    for (let i in this.countries) {
      if (this.countries[i].iso == localeIso) {
        foundIso = this.countries[i].iso;
        break;
      }
    }

    if (typeof this.initialValue === 'string' && this.initialValue.length > 0) {
      const initial = this.parseInitial(this.initialValue);
      if (typeof initial === 'object') {
        this.selectCode(initial.iso);
        this.phoneLocal = this.formatLocal(initial.phone);
      } else if (typeof foundIso === 'string') {
        this.selectCode(foundIso);
      } else {
        this.selectCode('us');
      }
    } else {
      if (typeof foundIso === 'string') {
        this.selectCode(foundIso);
      } else {
        this.selectCode('us');
      }
    }
    this.setValue();
  }

  /**
   * @readonly
   */
  static is = 'phone-input';

  @property({type: Array})
  countries;

  @property({type: Object})
  selected;

  @property({type: String})
  iso;

  @property({type: String})
  number;

  @property({type: Boolean})
  toggleList = false;

  @property({type: Boolean})
  hasScrolled = false;

  @property({type: String})
  phoneLocal = '';

  @property({type: String, attribute: true})
  initialValue = '';

  @property({type: String, attribute: true})
  value = '';

  @property({type: Boolean, attribute: true})
  disabled = false;

  @property({type: String, attribute: true})
  placeholder = '';

  @property({type: String, mapState: `${sdoNs.ORG_GROUP_INFO}.country`, store: storeInstance})
  groupCountry;

  /**
   *
   * @return {Object}
   */
  render() {
    return super.render(template, [styles]);
  }

  showList() {
    if (this.disabled) {
      return;
    }
    this.toggleList = !this.toggleList;
    setTimeout(() => {
      for (let i in this.shadowRoot.firstElementChild.firstElementChild.children[1].children) {
        let el = this.shadowRoot.firstElementChild.firstElementChild.children[1].children[i];
        if (typeof el.getAttribute === 'undefined') {
          continue;
        }
        if (el.getAttribute('data-iso') === this.selected.iso) {
          const vertical = this.hasScrolled ? 'nearest' : 'center';
          el.scrollIntoView({block: vertical, inline: 'nearest'});
          this.hasScrolled = true;
        }
      }
    }, 1);
  }

  hideList() {
    this.toggleList = false;
  }

  selectCode(iso, e) {
    this.selected = this.findCountry(iso);
    if (this.selected.phone_code === '1' && this.phoneLocal.length === 0
      && this.selected.iso !== 'ca' && this.selected.iso !== 'us') {
      this.phoneLocal = this.formatLocal(this.selected.all_codes[0].substring(2));
    }
    if (typeof e !== 'undefined') {
      e.target.parentNode.parentNode.parentNode.parentNode.querySelector('input').value = this.phoneLocal;
      e.target.parentNode.parentNode.parentNode.parentNode.querySelector('input').focus();
    }
    this.setValue();
    this.hideList();
  }

  findCountry(iso) {
    for (let i in this.countries) {
      if (this.countries[i].iso === iso) {
        return this.countries[i];
      }
    }
    return null;
  }

  setValue() {
    // Only add area code if there is a number set otherwise set as empty
    if (this.phoneLocal !== '') {
      this.value = '+' + this.selected.phone_code + ' ' + this.phoneLocal;
    } else {
      this.value = '';
    }
  }

  updatePhoneLocal(e) {
    if (typeof e.key === 'string' && e.key !== 'Backspace' && e.key !== 'Delete' && e.target.selectionStart == e.target.value.length) {
      e.target.value = this.formatLocal(e.target.value);
    }
    this.phoneLocal = e.target.value;
    this.setValue();
  }

  formatLocal(value) {
    let result = '';
    const justNumbers = value.replace(/\D/g, '');
    if (typeof this.selected !== 'undefined' && this.selected.phone_code === '1' && value.length > 0) {
      result = '(';
      result += justNumbers.substring(0, 3);
      if (justNumbers.length >= 3) {
        result += ') ';
      }
      result += justNumbers.substring(3, 6);
      if (justNumbers.length >= 6) {
        result += '-';
      }
      result += justNumbers.substring(6);
    } else {
      result = value;
    }
    return result;
  }

  parseInitial(value) {
    // the +01 format was to avoid maintaining all of Canada's area codes
    // this is no longer used and this code remains for legacy support
    if (value.substring(0, 3) === '+01') {
      return {iso: 'ca', phone: value.substring(3).trim()};
    }
    const justNumbers = value.replace(/\D/g, '');
    let result = null;
    for (let i in this.countries) {
      let country = this.countries[i];
      for (let j in country.all_codes) {
        let code = country.all_codes[j].replace(/\D/g, '');
        if (justNumbers.substring(0, code.length) === code) {
          if (value.substring(0, 1) === '+') {
            result = {iso: country.iso, phone: value.substring(1 + country.phone_code.length).trim()};
          } else {
            result = {iso: country.iso, phone: value.substring(country.phone_code.length).trim()};
          }
          // US only if no matches for other countries that start with +1
          if (country.iso !== 'us') {
            return result;
          }
        }
      }
    }
    return result;
  }
}

export default PhoneInput;
